import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { MapPin, Clock, PhoneCall, ArrowUpRight, Loader2, ShieldCheck } from "lucide-react";
import Reveal from "@/components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initial = { name: "", email: "", phone: "", message: "" };

const inputCls =
  "w-full rounded-xl border border-[#E3DCD3] bg-white px-4 py-3 text-sm text-[#2C2825] outline-none transition-all duration-300 placeholder:text-[#8C827A]/70 focus:border-[#C46B4E] focus:ring-2 focus:ring-[#C46B4E]/15";
const labelCls = "mb-2 block font-mono text-[11px] uppercase tracking-[0.18em] text-[#8C827A]";

const Contact = () => {
  const [form, setForm] = useState(initial);
  const [sending, setSending] = useState(false);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Thank you — your enquiry has been sent.", {
        description: "I aim to respond within 24–48 hours.",
      });
      setForm(initial);
    } catch (err) {
      toast.error("Something went wrong", {
        description: "Please try again in a moment.",
      });
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contact" className="py-20 md:py-32" data-testid="contact-section">
      <div className="mx-auto grid max-w-7xl grid-cols-1 gap-14 px-6 sm:px-8 lg:grid-cols-5 lg:gap-16 lg:px-12">
        <div className="lg:col-span-3">
          <Reveal>
            <p className="mb-4 font-mono text-xs uppercase tracking-[0.22em] text-[#8C827A]">Contact</p>
            <h2 className="font-serif text-3xl font-normal tracking-tight text-[#2C2825] sm:text-4xl lg:text-5xl">
              Begin with a <span className="italic text-[#C46B4E]">conversation</span>
            </h2>
            <p className="mt-6 max-w-xl text-base font-light leading-relaxed text-[#5E564F] sm:text-lg">
              Reaching out can feel like the hardest step. Send a short message and we can
              arrange an initial consultation — no obligation, at your pace.
            </p>
          </Reveal>

          <Reveal delay={0.1}>
            <form onSubmit={submit} className="mt-10 space-y-6" data-testid="contact-form">
              <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                <div>
                  <label htmlFor="contact-name" className={labelCls}>Full name</label>
                  <input id="contact-name" data-testid="contact-input-name" required value={form.name} onChange={set("name")} placeholder="Your name" className={inputCls} />
                </div>
                <div>
                  <label htmlFor="contact-email" className={labelCls}>Email address</label>
                  <input id="contact-email" data-testid="contact-input-email" required type="email" value={form.email} onChange={set("email")} placeholder="you@example.com" className={inputCls} />
                </div>
              </div>
              <div>
                <label htmlFor="contact-phone" className={labelCls}>Phone (optional)</label>
                <input id="contact-phone" data-testid="contact-input-phone" type="tel" value={form.phone} onChange={set("phone")} placeholder="+44 ..." className={inputCls} />
              </div>
              <div>
                <label htmlFor="contact-message" className={labelCls}>Message</label>
                <textarea id="contact-message" data-testid="contact-textarea-message" required rows={5} value={form.message} onChange={set("message")} placeholder="A few words about what brings you here — as much or as little as you like." className={`${inputCls} resize-none`} />
              </div>
              <button
                type="submit"
                disabled={sending}
                data-testid="contact-submit-button"
                className="group inline-flex items-center gap-2 rounded-full bg-[#C46B4E] px-8 py-3.5 text-sm font-medium text-[#FAF8F5] transition-colors duration-300 hover:bg-[#B05C41] disabled:cursor-not-allowed disabled:opacity-60"
              >
                {sending ? (
                  <>
                    Sending
                    <Loader2 className="h-4 w-4 animate-spin" />
                  </>
                ) : (
                  <>
                    Send enquiry
                    <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
                  </>
                )}
              </button>
            </form>
          </Reveal>
        </div>

        <div className="lg:col-span-2">
          <Reveal delay={0.15} className="lg:sticky lg:top-28">
            <div className="space-y-5">
              <div className="rounded-2xl border border-[#E3DCD3] bg-white p-7" data-testid="contact-info-location">
                <MapPin className="mb-4 h-5 w-5 text-[#C46B4E]" strokeWidth={1.5} />
                <h3 className="mb-2 text-lg font-semibold text-[#2C2825]">The practice</h3>
                <p className="text-sm leading-relaxed text-[#5E564F]">
                  An online-only practice — offering secure video therapy across the UK and internationally, from the comfort of your own space.
                </p>
              </div>
              <div className="rounded-2xl border border-[#E3DCD3] bg-white p-7" data-testid="contact-info-response">
                <Clock className="mb-4 h-5 w-5 text-[#C46B4E]" strokeWidth={1.5} />
                <h3 className="mb-2 text-lg font-semibold text-[#2C2825]">Response time</h3>
                <p className="text-sm leading-relaxed text-[#5E564F]">
                  I read every enquiry personally and aim to respond within 24–48 hours, Monday to Friday.
                </p>
              </div>
              <div className="rounded-2xl border border-[#E3DCD3] bg-white p-7" data-testid="contact-privacy-note">
                <ShieldCheck className="mb-4 h-5 w-5 text-[#7A8B7B]" strokeWidth={1.5} />
                <h3 className="mb-2 text-lg font-semibold text-[#2C2825]">Your privacy</h3>
                <p className="text-sm leading-relaxed text-[#5E564F]">
                  I am a registered data controller with the Information Commissioner&rsquo;s Office (ICO)
                  and comply with all GDPR requirements — all data you provide is handled in the
                  strictest confidence and stored safely.
                </p>
              </div>
              <div className="rounded-2xl border border-[#7A8B7B]/30 bg-[#E8EFE8] p-7" data-testid="contact-crisis-note">
                <PhoneCall className="mb-4 h-5 w-5 text-[#7A8B7B]" strokeWidth={1.5} />
                <h3 className="mb-2 text-lg font-semibold text-[#2C2825]">In crisis?</h3>
                <p className="text-sm leading-relaxed text-[#5E564F]">
                  This service is not for emergencies. If you need immediate support, please contact
                  Samaritans on <strong>116 123</strong> (UK, 24/7) or call <strong>999</strong>.
                </p>
              </div>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

export default Contact;
